import React, { useState, useMemo } from "react";
import uniqueId from "lodash.uniqueid";
import { useTranslation } from "react-i18next";
import {
  DraggableProvidedDragHandleProps,
  DropResult,
} from "react-beautiful-dnd";
import { FieldArrayRenderProps, useFormikContext } from "formik";
import {
  Add,
  Delete,
  Drag,
  Edit,
  Info,
  Top,
} from "@hitachivantara/uikit-react-icons";
import {
  HvGrid,
  HvButton,
  HvTypography,
  HvGlobalActions,
  HvTooltip,
  HvEmptyState,
  useScrollTo,
} from "@hitachivantara/uikit-react-core";
import {
  HvTableColumnConfig,
  HvCellProps,
} from "@hitachivantara/uikit-react-lab";

import { OverflowTooltip, DragDropList } from "components/common";
import { actionsColumn, textColumn } from "components/common/SimpleTable";
import { AttributeFormData, IncidentTypeFormData } from "lib/api";

import useStyles from "./styles";
import AddEditPropertiesDialog from "../AddEditPropertiesDialog";

type AttributeDataRow = Record<string, unknown> & AttributeFormData;

const IncidentTypeProperties: React.FC<FieldArrayRenderProps> = ({
  push,
  remove,
  replace,
  move,
}) => {
  const { t } = useTranslation("addEditIncidentType");
  const classes = useStyles();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [mode, setMode] = useState<"add" | "edit">("add");
  const [dialogValues, setDialogValues] = useState<
    AttributeFormData | undefined
  >();
  const {
    values: { attributes },
  } = useFormikContext<IncidentTypeFormData>();

  const openDialog = (initialValues?: AttributeFormData) => {
    setMode(initialValues ? "edit" : "add");
    setDialogValues(initialValues);
    setIsDialogOpen(true);
  };

  const columns = useMemo(
    (): HvTableColumnConfig<AttributeDataRow>[] => [
      {
        Header: t("properties.columns.id") as string,
        accessor: "id",
        style: { width: "10%" },
        sortType: "alphanumeric",
      },
      textColumn({
        Header: t("properties.columns.label") as string,
        accessor: "label",
        style: { width: "40%" },
      }),
      textColumn({
        Header: t("properties.columns.type") as string,
        accessor: "type",
        style: { width: "25%" },
        Cell: ({
          row: {
            original: { type },
          },
        }: HvCellProps<AttributeDataRow, string>) => (
          <OverflowTooltip
            data={
              t(
                `propertiesDialog.fields.type.options.${(
                  type as string
                ).replaceAll(" ", "_")}`
              ) as string
            }
          />
        ),
      }),
      textColumn({
        Header: t("properties.columns.visibility") as string,
        accessor: "visibility",
        style: { width: "25%" },
        Cell: ({
          row: {
            original: { visibility },
          },
        }: HvCellProps<AttributeDataRow, string>) => (
          <OverflowTooltip
            data={
              t(
                `propertiesDialog.fields.visibility.options.${
                  visibility as string
                }`
              ) as string
            }
          />
        ),
      }),
      actionsColumn({
        id: "actions",
        Cell: ({
          row: {
            original: { id, label, type, visibility },
          },
          dragHandleProps,
        }: HvCellProps<AttributeDataRow> & {
          dragHandleProps: DraggableProvidedDragHandleProps;
        }) => {
          return (
            <div className={classes.actionButtons}>
              <HvTooltip
                title={
                  <HvTypography>
                    {t("properties.columns.actions.edit")}
                  </HvTypography>
                }
              >
                <HvButton
                  icon
                  category="ghost"
                  aria-label={t("properties.columns.actions.drag")}
                  onClick={() =>
                    openDialog({
                      id,
                      label,
                      type,
                      visibility,
                    })
                  }
                >
                  <Edit />
                </HvButton>
              </HvTooltip>
              <HvTooltip
                title={
                  <HvTypography>
                    {t("properties.columns.actions.drag")}
                  </HvTypography>
                }
              >
                <HvButton
                  icon
                  category="ghost"
                  aria-label={t("properties.columns.actions.drag")}
                  {...{ component: "div" }}
                  {...dragHandleProps}
                >
                  <Drag />
                </HvButton>
              </HvTooltip>
            </div>
          );
        },
      }),
    ],
    [t, classes]
  );

  const [, setScrollTo] = useScrollTo(
    0,
    "wis-container",
    false,
    100,
    [
      {
        label: t("steps.title"),
        value: "steps",
      },
      {
        value: "topButton",
      },
    ],
    undefined
  );

  const handleAddEditProperty = (
    attribute: AttributeFormData,
    addAnother: boolean | undefined
  ) => {
    if (
      attributes.findIndex((attr) => attr.label === attribute.label) > -1 &&
      mode === "add"
    ) {
      throw new Error("name_already_exists");
    }

    if (mode === "add") {
      const attr = { ...attribute, id: `key${uniqueId()}` };
      push(attr);
      setScrollTo(null, "topButton", 0, undefined);
      setIsDialogOpen(addAnother || false);

      return {
        ...attr,
        order: attributes.length,
      };
    }

    replace(
      attributes.findIndex((s) => s.id === attribute.id),
      attribute
    );
    setIsDialogOpen(addAnother || false);
    return undefined;
  };

  const handleDelete = (selectedRowIds: Record<string, boolean>) => {
    const selectedRowOrders = Object.keys(selectedRowIds);
    let numberDeleted = 0;

    attributes.forEach((attr, index) => {
      if (selectedRowOrders.includes(attr.id as string)) {
        // remove updates the indexes inside formik, so we need to update indexes while we remove
        remove(index - numberDeleted);
        numberDeleted += 1;
      }
    });
  };

  const onDragEndHandler = (result: DropResult) => {
    move(result.source.index, result.destination?.index as number);
  };

  return (
    <HvGrid container spacing={2}>
      <HvGrid sm={12} item>
        <HvGlobalActions
          title={t("sections.properties.title")}
          variant="section"
        />
        <HvTypography variant="normalText">
          {t("sections.properties.description")}
        </HvTypography>
      </HvGrid>
      <HvGrid sm={12} item>
        <HvGrid container justifyContent="flex-end">
          <HvGrid item>
            <HvButton
              category="semantic"
              startIcon={<Add />}
              onClick={() => openDialog()}
            >
              {t("properties.buttons.add")}
            </HvButton>
          </HvGrid>
        </HvGrid>

        <AddEditPropertiesDialog
          isOpen={isDialogOpen}
          onClose={() => setIsDialogOpen(false)}
          onSave={handleAddEditProperty}
          initialValues={dialogValues}
          mode={mode}
        />
      </HvGrid>
      <HvGrid sm={12} item>
        <DragDropList
          columns={columns}
          data={attributes}
          actions={[
            {
              id: "delete",
              label: t("properties.buttons.delete"),
              icon: <Delete />,
            },
          ]}
          actionsCallback={handleDelete}
          emptyState={
            <HvEmptyState
              title={t("properties.emptyState.title")}
              message={t("properties.emptyState.description")}
              icon={<Info />}
            />
          }
          onDragEnd={onDragEndHandler}
        />

        {attributes.length >= 10 && (
          <HvGrid container justifyContent="flex-end">
            <HvGrid item>
              <HvTooltip title={<HvTypography>Go to Top</HvTypography>}>
                <HvButton
                  id="topButton"
                  category="semantic"
                  aria-label={t("properties.buttons.top.description")}
                  startIcon={<Top />}
                  onClick={(event) => setScrollTo(event, "steps", 0, undefined)}
                >
                  {t("properties.buttons.top.label")}
                </HvButton>
              </HvTooltip>
            </HvGrid>
          </HvGrid>
        )}
      </HvGrid>
    </HvGrid>
  );
};

export default IncidentTypeProperties;
